import { MAIN_TYPES } from "../Reducers/MainReducer";
import { ExerciseInterface } from "../../../interfaces/examInterfaces";
import { CREATE_PAGES_TYPES } from "../Reducers/CreatePagesReducer";
import { fetchImageUrl } from "../../../firebase/firebaseExamService";
import { ExerciseHeight } from "../ExamBuilder";
import { getAppDispatch } from "../../../stores/dispatchStore";
import { handleCloseDialog, setExerciseHeights, setPages, setPagesCreated, setTotalPoints } from "./FunctionsMain";
import { RefObject } from "react";
import { LayoutExamTypeKeys } from "../../../enums/layoutExamTypes";
import { DIALOG_TYPES, DIALOGS } from "../../Dialogs/dialogReducer";

export function measureExercisesContainerHight(
  containerRef: RefObject<HTMLDivElement>,
  examHeaderRef: RefObject<HTMLDivElement>,
  totalPointsRef: RefObject<HTMLDivElement>
) {
  if (containerRef.current && examHeaderRef.current && totalPointsRef.current) {
    const containerHeight = containerRef.current.offsetHeight;
    const headerHeight = examHeaderRef.current.offsetHeight;
    const totalPointsHeight = totalPointsRef.current.offsetHeight;
    setContainerHeight(containerHeight);
    setHeaderHeight(headerHeight);
    setTotalPointsHeight(totalPointsHeight);
  }
}

export async function getImageUrls(
  exercises: ExerciseInterface[],
  imageUrls: Record<string, string>,
  solutionImageUrls: Record<string, string>,
  isDemoMode: boolean
) {
  for (const exercise of exercises) {
    if (!imageUrls[exercise.id]) {
      const imagePath = exercise.imagePath;
      let imageUrl: string | undefined = "";

      if (isDemoMode) {
        imageUrl = imagePath;
      } else {
        imageUrl = await fetchImageUrl(imagePath);
      }

      if (imageUrl) {
        setImageUrl(exercise.id, imageUrl);
      }
    }

    if (!solutionImageUrls[exercise.id]) {
      const solutionImagePath = exercise.imagePathSolution;
      let imageUrlSolution: string | undefined = "";

      if (isDemoMode) {
        imageUrlSolution = solutionImagePath;
      } else {
        imageUrlSolution = await fetchImageUrl(solutionImagePath);
      }

      if (imageUrlSolution) {
        setSolutionImageUrl(exercise.id, imageUrlSolution);
      }
    }
  }
}

export function addExerciseHeight(exerciseHeights: Record<string, number>, heightData: ExerciseHeight) {
  setExerciseHeights(exerciseHeights, { id: heightData.id, height: heightData.height });
}

export function createPages(
  sortedExercises: ExerciseInterface[],
  containerHeight: number,
  exerciseHeights: Record<string, number>,
  headerHeight: number,
  totalPointsHeight: number
) {
  if (sortedExercises) {
    let pages: ExerciseInterface[][] = [];
    let currentPage: ExerciseInterface[] = [];
    let remainingContainerHeight = containerHeight + totalPointsHeight;
    let remainingHeight = 0;

    sortedExercises.forEach((exercise, index) => {
      let exerciseHeight = exerciseHeights[exercise.id];
      remainingHeight = remainingContainerHeight - exerciseHeight;
      const isLastExercise = index + 1 >= sortedExercises.length;
      const fitsInCurrentPage = isLastExercise ? remainingHeight - totalPointsHeight > 5 : remainingHeight > 5;

      if (fitsInCurrentPage) {
        currentPage.push(exercise);
        remainingContainerHeight = remainingHeight;
      } else {
        pages.push(currentPage);
        currentPage = [exercise];
        remainingContainerHeight = containerHeight - exerciseHeight;
        if (pages.length >= 1) {
          remainingContainerHeight += headerHeight;
        }
      }
    });
    pages.push(currentPage);
    setPages(pages);
    setPagesCreated(true);
  }
}

export function calculateTotalPoints(sortedExercises: ExerciseInterface[]) {
  let totalPoints: number = 0;
  sortedExercises.forEach((exercise) => {
    totalPoints += exercise.points;
  });
  setTotalPoints(totalPoints);
}

export function setContainerHeight(containerHeight: number) {
  const dispatch = getAppDispatch();
  dispatch({ type: CREATE_PAGES_TYPES.CONTAINER_HEIGHT, payload: containerHeight });
}

export function setIsMeasuringContainer(isMeasuringContainer: boolean) {
  const dispatch = getAppDispatch();
  dispatch({ type: CREATE_PAGES_TYPES.IS_MEASURING_CONTAINER, payload: isMeasuringContainer });
}

export function setIsMeasuringExercises(isMeasuringExercises: boolean) {
  const dispatch = getAppDispatch();
  dispatch({ type: CREATE_PAGES_TYPES.IS_MEASURING_EXERCISES, payload: isMeasuringExercises });
}

export function setLayoutType(layoutType: LayoutExamTypeKeys) {
  const dispatch = getAppDispatch();
  dispatch({ type: CREATE_PAGES_TYPES.LAYOUT_TYPE, payload: layoutType });
  handleCloseDialog(DIALOGS.CHANGE_LAYOUT_DIALOG);
}

function setImageUrl(exerciseId: string, imageUrl: string) {
  const dispatch = getAppDispatch();
  dispatch({ type: MAIN_TYPES.IMAGE_URLS, payload: { id: exerciseId, url: imageUrl } });
}

function setSolutionImageUrl(exerciseId: string, imageUrl: string) {
  const dispatch = getAppDispatch();
  dispatch({
    type: MAIN_TYPES.SOLUTION_IMAGE_URLS,
    payload: { id: exerciseId, url: imageUrl },
  });
}

function setHeaderHeight(headerHeight: number) {
  const dispatch = getAppDispatch();
  dispatch({
    type: CREATE_PAGES_TYPES.HEADER_HEIGHT,
    payload: headerHeight,
  });
}

function setTotalPointsHeight(totalPointsHeight: number) {
  const dispatch = getAppDispatch();
  dispatch({
    type: CREATE_PAGES_TYPES.TOTAL_POINTS_HEIGHT,
    payload: totalPointsHeight,
  });
}
